import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

/** `10.0` → `"10"`, `8.93` → `"8.9"`. Matches design that strips trailing `.0`. */
export function formatLeverage(value: number): string {
  const fixed = value.toFixed(1);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
}

export const ARROW_SX = { fontSize: 16, color: 'text.primary', flexShrink: 0 } as const;

/** A single label / value row in the info section. Label is secondary; values come from `children`. */
export function InfoRow({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body1" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>{children}</Box>
    </Box>
  );
}

/**
 * `current` value, optionally followed by an arrow + `projected` value.
 * Direction = 'up' uses NorthEastIcon; 'down' uses SouthEastIcon; undefined renders no arrow.
 */
export function ValueWithProjection({
  current,
  projected,
  projectedDirection,
}: {
  current: ReactNode;
  projected?: ReactNode;
  projectedDirection?: 'up' | 'down';
}) {
  return (
    <>
      <Typography variant="body1" color="text.primary" className="numeric">
        {current}
      </Typography>
      {projected != null && (
        <>
          {projectedDirection === 'up' && <NorthEastIcon fontSize="small" sx={ARROW_SX} />}
          {projectedDirection === 'down' && <SouthEastIcon fontSize="small" sx={ARROW_SX} />}
          <Typography variant="body1" color="text.primary" className="numeric">
            {projected}
          </Typography>
        </>
      )}
    </>
  );
}
